import i18n from '../i18n';
import { getDocument } from './documents';
import { canAccessLocation, getGroupLocation, getMyDrive, ONEDRIVE_SITE_ID } from './graph';
import { readLastOneDriveFolder } from './lastOneDriveFolder';
import { getTemplate } from './templates';
import type { SharePointLocation } from './types';

/** What the save screen needs to know about a generated document and the template behind it. */
export interface SaveContext {
  documentId: string;
  fileName: string;
  templateId: string;
  templateLocation: SharePointLocation | null;
  saveToCompanyTeamSite: boolean;
  companyGroupId: string | null;
  companyName: string;
}

export interface ResolvedDestination {
  location: SharePointLocation | null;
  /** Why the picker did not open on the template's choice, when it did not. */
  notice: { key: string; params?: Record<string, string> } | null;
}

/** Template data only. A template that cannot be read just means no suggestion. */
export async function loadSaveContext(documentId: string): Promise<SaveContext> {
  const document = await getDocument(documentId);
  const detail = await getTemplate(document.templateId).catch(() => null);

  return {
    documentId,
    fileName: document.fileName,
    templateId: document.templateId,
    templateLocation: detail?.template.sharePointLocation ?? null,
    saveToCompanyTeamSite: detail?.template.saveToCompanyTeamSite ?? false,
    companyGroupId: document.companyGroupId ?? null,
    companyName: document.companyName ?? ''
  };
}

/**
 * Where the picker opens, by the signed-in user's own access: the company's team site, then the
 * template's location, then the last OneDrive folder used for this template, then their OneDrive.
 * `sharepoint` skips the OneDrive steps.
 */
export async function resolveSaveDestination(
  context: SaveContext,
  mode: 'default' | 'sharepoint',
  user: string
): Promise<ResolvedDestination> {
  let notice: ResolvedDestination['notice'] = null;

  if (context.saveToCompanyTeamSite && context.companyGroupId) {
    const location = await getGroupLocation(context.companyGroupId, context.companyName).catch(() => null);
    if (location) return { location, notice: null };
    notice = { key: 'sharePoint.save.unreachableCompany', params: { company: context.companyName } };
  }

  if (context.templateLocation) {
    if (await canAccessLocation(context.templateLocation)) return { location: context.templateLocation, notice: null };
    notice ??= { key: 'sharePoint.save.unreachableTemplate' };
  }

  if (mode === 'sharepoint') return { location: null, notice };

  const drive = await getMyDrive().catch(() => null);
  if (!drive) return { location: null, notice };

  const remembered = readLastOneDriveFolder(user, context.templateId);
  if (remembered && remembered.driveId === drive.id && (await canAccessLocation(remembered))) {
    return { location: remembered, notice };
  }

  return {
    location: {
      siteId: ONEDRIVE_SITE_ID,
      siteName: i18n.t('sharePoint.picker.oneDrive'),
      driveId: drive.id,
      driveName: drive.name,
      folders: []
    },
    notice
  };
}
